'use client';

import { useState } from 'react';
import { VStack, HStack } from '@terra/ui-styled-system';
import { Text } from '../../../components/text';
import { Button } from '../../../components/button';
import { Collapsible } from '../../../components/collapsible';
import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  CVSS_SECURITY_REQUIREMENT_METRIC_KEYS,
  CVSS_SECURITY_REQUIREMENT_VALUES,
  normalizeEnvironmentalRequirements,
  type CvssEnvironmentalRequirements,
  type CvssSecurityRequirementMetricKey,
  type CvssVersion,
} from '@terra/shared-cvss';
import { MetricSelector } from './MetricSelector';
import {
  getModifiedMetricAllowedValues,
  getModifiedMetricKeysForVersion,
  getModifiedMetricValueLabelKey,
  type CvssModifiedMetricKey,
} from '../environmental-metrics';

type EnvironmentalMetricsPanelProps = {
  requirements: Partial<CvssEnvironmentalRequirements>;
  version: Exclude<CvssVersion, '3.0'>;
  includeModifiedMetrics?: boolean;
};
const SECURITY_REQUIREMENT_TITLE_KEYS = {
  cr: 'metrics.confidentialityRequirement',
  ir: 'metrics.integrityRequirement',
  ar: 'metrics.availabilityRequirement',
} as const satisfies Record<CvssSecurityRequirementMetricKey, string>;

export function EnvironmentalMetricsPanel({
  requirements,
  version,
  includeModifiedMetrics,
}: EnvironmentalMetricsPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAdditionalOpen, setIsAdditionalOpen] = useState(false);
  const t = useTranslations('designSystem.cvss.calculator');
  const resolved = normalizeEnvironmentalRequirements(requirements);
  const showModifiedMetrics = includeModifiedMetrics !== false;
  const modifiedMetricKeys = showModifiedMetrics
    ? getModifiedMetricKeysForVersion(version)
    : [];
  const requirementOptions = CVSS_SECURITY_REQUIREMENT_VALUES.map((value) => ({
    label: t(`requirements.${value}`),
    value,
  }));
  const modifiedMetricValueLabel = ({
    key,
    value,
  }: {
    key: CvssModifiedMetricKey;
    value: string;
  }) =>
    t(
      getModifiedMetricValueLabelKey({
        key,
        value,
      }) as Parameters<typeof t>[0],
    );

  return (
    <Collapsible.Root
      open={isExpanded}
      onOpenChange={({ open }) => setIsExpanded(open)}
      w="full"
    >
      <Collapsible.Trigger asChild>
        <Button
          size="sm"
          variant="ghost"
          fontWeight="medium"
          justifyContent="flex-start"
          px={0}
          _hover={{ bg: 'transparent' }}
        >
          <HStack gap={2}>
            {isExpanded ? (
              <ArrowDown01Icon size={14} />
            ) : (
              <ArrowRight01Icon size={14} />
            )}
            <Text fontSize="xs" fontWeight="medium">
              {t('environmentalTitle')}
            </Text>
          </HStack>
        </Button>
      </Collapsible.Trigger>

      <Collapsible.Content>
        <VStack gap={3} w="full" pt={2} alignItems="stretch">
          <Text fontSize="2xs" color="fg.muted">
            {t('environmentalViewOnly')}
          </Text>
          {CVSS_SECURITY_REQUIREMENT_METRIC_KEYS.map((key) => (
            <MetricSelector
              key={key}
              title={t(SECURITY_REQUIREMENT_TITLE_KEYS[key])}
              options={requirementOptions}
              value={resolved[key]}
              onChange={() => undefined}
              readOnly
            />
          ))}

          {showModifiedMetrics ? (
            <Collapsible.Root
              open={isAdditionalOpen}
              onOpenChange={({ open }) => setIsAdditionalOpen(open)}
            >
              <Collapsible.Trigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  fontWeight="medium"
                  justifyContent="flex-start"
                  px={0}
                  _hover={{ bg: 'transparent' }}
                >
                  <HStack gap={2}>
                    {isAdditionalOpen ? (
                      <ArrowDown01Icon size={14} />
                    ) : (
                      <ArrowRight01Icon size={14} />
                    )}
                    <Text fontSize="xs" fontWeight="medium">
                      {t('additionalModifiers')}
                    </Text>
                  </HStack>
                </Button>
              </Collapsible.Trigger>
              <Collapsible.Content>
                <VStack gap={3} w="full" pt={2} alignItems="stretch">
                  {modifiedMetricKeys.map((key) => {
                    const allowedValues = getModifiedMetricAllowedValues({
                      key,
                      version,
                    });
                    const storedValue = resolved[key];
                    const selectValue = allowedValues.includes(storedValue)
                      ? storedValue
                      : 'X';

                    return (
                      <MetricSelector
                        key={key}
                        title={t(`modified.${key}`)}
                        options={allowedValues.map((value) => ({
                          label: modifiedMetricValueLabel({ key, value }),
                          value,
                        }))}
                        value={selectValue}
                        onChange={() => undefined}
                        readOnly
                      />
                    );
                  })}
                </VStack>
              </Collapsible.Content>
            </Collapsible.Root>
          ) : null}
        </VStack>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
